import { useEffect, useRef, useState } from "react";
import { Send, AtSign } from "lucide-react";
import api from "@/lib/api";
import { Avatar, timeAgo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function renderWithMentions(text, members) {
  if (!members?.length) return text;
  const matches = [];
  members.forEach((m) => {
    const token = "@" + m.name;
    let i = text.indexOf(token);
    while (i !== -1) {
      matches.push([i, token.length]);
      i = text.indexOf(token, i + token.length);
    }
  });
  if (!matches.length) return text;
  matches.sort((a, b) => a[0] - b[0]);
  const out = [];
  let pos = 0;
  matches.forEach(([start, len], k) => {
    if (start < pos) return;
    if (start > pos) out.push(text.slice(pos, start));
    out.push(<span key={k} className="text-cobalt font-semibold">{text.slice(start, start + len)}</span>);
    pos = start + len;
  });
  if (pos < text.length) out.push(text.slice(pos));
  return out;
}

export function ProjectChat({ projectId, members = [] }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const seqRef = useRef(0);
  const busyRef = useRef(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [typing, setTyping] = useState([]);
  const lastTypingSent = useRef(0);

  const poll = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const r = await api.get(`/build/projects/${projectId}/chat`, { params: { since: seqRef.current } });
      setTyping(r.data.typing || []);
      if (r.data.messages.length) {
        seqRef.current = r.data.messages[r.data.messages.length - 1].seq;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          const fresh = r.data.messages.filter((m) => !seen.has(m.message_id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch (e) {} finally {
      busyRef.current = false;
    }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [projectId]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2000) return;
    lastTypingSent.current = now;
    api.post(`/build/projects/${projectId}/chat/typing`).catch(() => {});
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    const mentions = members.filter((m) => t.includes("@" + m.name)).map((m) => m.user_id);
    setText("");
    setMentionQuery(null);
    try {
      const r = await api.post(`/build/projects/${projectId}/chat`, { text: t, mentions });
      seqRef.current = Math.max(seqRef.current, r.data.seq);
      setMessages((prev) => (prev.some((m) => m.message_id === r.data.message_id) ? prev : [...prev, r.data]));
    } catch (e) {}
  };

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    sendTyping();
    const m = v.slice(0, e.target.selectionStart).match(/@([^\s@]*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const pickMention = (member) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@([^\s@]*)$/, "@" + member.name + " ");
    const after = text.slice(caret);
    setText(before + after);
    setMentionQuery(null);
    setTimeout(() => el?.focus(), 0);
  };

  const otherMembers = members.filter((m) => m.user_id !== user?.user_id);
  const filtered = mentionQuery !== null
    ? otherMembers.filter((m) => m.name.toLowerCase().includes(mentionQuery))
    : [];

  return (
    <div className="border border-border bg-card rounded-xl flex flex-col h-[600px]" data-testid="project-chat">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <span className="font-serif text-lg font-bold">Discussion</span>
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" /> live
        </span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No messages yet. Say hello 👋</p>
        ) : messages.map((m) => {
          const mine = m.author_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.message_id}`}>
              <Avatar src={m.author.picture} name={m.author.name} size={30} />
              <div className={`max-w-[75%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                <div className={`text-xs text-muted-foreground ${mine ? "flex-row-reverse" : ""} flex gap-2`}>
                  <span className="font-medium text-foreground">{mine ? "You" : m.author.name}</span>
                  <span>{timeAgo(m.created_at)}</span>
                </div>
                <div className={`mt-1 text-sm px-3 py-2 rounded-lg whitespace-pre-wrap break-words ${mine ? "bg-cobalt text-white" : "bg-secondary"}`}>
                  {renderWithMentions(m.text, members)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {typing.length > 0 && (
        <div className="px-5 py-1.5 text-xs text-muted-foreground italic flex items-center gap-1.5" data-testid="typing-indicator">
          <span className="flex gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cobalt animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-cobalt animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-cobalt animate-bounce" style={{ animationDelay: "300ms" }} />
          </span>
          {typing.length === 1 ? `${typing[0]} is typing…`
            : typing.length === 2 ? `${typing[0]} and ${typing[1]} are typing…`
            : `${typing.length} people are typing…`}
        </div>
      )}
      <div className="p-3 border-t border-border relative">
        {filtered.length > 0 && (
          <div className="absolute bottom-full left-3 right-3 mb-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-20" data-testid="mention-dropdown">
            <div className="px-3 py-1.5 text-xs text-muted-foreground flex items-center gap-1.5 border-b border-border"><AtSign size={12} /> Mention a teammate</div>
            {filtered.map((m) => (
              <button key={m.user_id} onClick={() => pickMention(m)} data-testid={`mention-option-${m.user_id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-secondary/60 transition-colors">
                <Avatar src={m.picture} name={m.name} size={26} />
                <span>{m.name}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input ref={inputRef} value={text} onChange={onChange} onKeyDown={(e) => e.key === "Enter" && filtered.length === 0 && send()}
            placeholder="Message the team… use @ to mention" data-testid="chat-input"
            className="flex-1 bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors" />
          <button onClick={send} data-testid="chat-send" className="bg-cobalt text-white rounded-md p-2.5 hover:bg-gold-hover transition-colors">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
